<template>
  <div>
    <h2
      class="font-[Poppins] font-black text-[clamp(24px,3.5vw,34px)] text-[#191c1e] mb-1.5 tracking-[-0.5px]"
    >
      Segmenterad kontroll
    </h2>
    <p
      class="font-[Plus_Jakarta_Sans] text-[15px] text-[#6c7a77] leading-[1.65] mb-7 max-w-[580px]"
    >
      Används i Degkalkylatorn. Primärversionen är en pill-knapp med skugga för
      val av degtyp; sekundärversionen är en kantad variant för jästtyp.
    </p>

    <PreviewBox label="Primär — grå bakgrund, vit aktiv (degtyp)">
      <div class="flex gap-2 p-1 rounded-2xl" style="background-color: #f0f2f4">
        <button
          v-for="m in modes"
          :key="m.id"
          class="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-[Poppins] font-bold text-sm cursor-pointer border-none transition-all"
          :style="{
            backgroundColor: segMode === m.id ? 'white' : 'transparent',
            color: segMode === m.id ? '#191c1e' : '#6c7a77',
            boxShadow: segMode === m.id ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
          }"
          @click="segMode = m.id"
        >
          <span>{{ m.emoji }}</span> {{ m.label }}
        </button>
      </div>
    </PreviewBox>

    <PreviewBox label="Sekundär — individuella kantade pills (jästtyp)">
      <div class="flex gap-2">
        <button
          v-for="y in yeastTypes"
          :key="y.id"
          class="flex-1 px-4 py-2 rounded-xl text-center cursor-pointer transition-all whitespace-nowrap"
          :style="{
            border: `1.5px solid ${yeastType === y.id ? '#14b8a6' : '#eceef0'}`,
            backgroundColor:
              yeastType === y.id ? 'rgba(20,184,166,0.06)' : 'white',
            fontFamily: '\'Plus Jakarta Sans\', sans-serif',
            fontWeight: 700,
            fontSize: '12px',
            color: yeastType === y.id ? '#14b8a6' : '#6c7a77',
          }"
          @click="yeastType = y.id"
        >
          {{ y.label }}
        </button>
      </div>
    </PreviewBox>

    <CodeBlock :tabs="snippetTabs" />
  </div>
</template>

<script setup>
import { ref } from "vue";
import PreviewBox from "../components/PreviewBox.vue";
import CodeBlock from "../components/CodeBlock.vue";

const htmlCode = `<!-- Primär — grå bakgrund, vit aktiv (degtyp) -->
<div class="flex gap-2 p-1 rounded-2xl" style="background-color: #f0f2f4">
  <button
    v-for="m in modes" :key="m.id"
    class="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-[Poppins] font-bold text-sm cursor-pointer border-none transition-all"
    :style="{
      backgroundColor: active === m.id ? 'white' : 'transparent',
      color: active === m.id ? '#191c1e' : '#6c7a77',
      boxShadow: active === m.id ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
    }"
    @click="active = m.id"
  >
    <span>{{ m.emoji }}</span> {{ m.label }}
  </button>
</div>

<!-- Sekundär — individuella kantade pills (jästtyp) -->
<div class="flex gap-2">
  <button
    v-for="y in yeastTypes" :key="y.id"
    class="flex-1 px-4 py-2 rounded-xl text-center cursor-pointer transition-all"
    :style="{
      border: \`1.5px solid \${active === y.id ? '#14b8a6' : '#eceef0'}\`,
      backgroundColor: active === y.id ? 'rgba(20,184,166,0.06)' : 'white',
      fontFamily: '\'Plus Jakarta Sans\', sans-serif',
      fontWeight: 700,
      fontSize: '12px',
      color: active === y.id ? '#14b8a6' : '#6c7a77',
    }"
    @click="active = y.id"
  >
    {{ y.label }}
  </button>
</div>`;
const cssCode = `/* Primär — wrapper */
background: #f0f2f4;
border-radius: 16px;  /* rounded-2xl */
padding: 4px;         /* p-1 */

/* Primär — aktiv knapp */
background: white;
color: #191c1e;
box-shadow: 0 1px 4px rgba(0,0,0,0.10);

/* Primär — inaktiv knapp */
background: transparent;
color: #6c7a77;

/* Sekundär — aktiv pill */
border: 1.5px solid #14b8a6;
background: rgba(20,184,166,0.06);
color: #14b8a6;
font-family: 'Plus Jakarta Sans', sans-serif;
font-size: 12px;

/* Sekundär — inaktiv pill */
border: 1.5px solid #eceef0;
background: white;
color: #6c7a77;`;

const snippetTabs = [
  { label: "HTML", code: htmlCode },
  { label: "CSS", code: cssCode, lang: "css" },
];
const segMode = ref("yeast");
const yeastType = ref("fresh");

const modes = [
  { id: "yeast", label: "Jäst-deg", emoji: "🍞" },
  { id: "sourdough", label: "Surdeg", emoji: "🌾" },
  { id: "enriched", label: "Berikad deg", emoji: "🧈" },
];
const yeastTypes = [
  { id: "fresh", label: "Färsk jäst" },
  { id: "dry", label: "Torrjäst" },
  { id: "instant", label: "Snabbjäst" },
];
</script>
